<template>
  <RevViewWrapper
    revType="quickAction"
    :organizationId="organizationId"
    :revId="revId"
    :mode="mode"
    :rev="rev"
    :resetRev="resetRev"
    :get="get"
    :update="update"
    :like="like"
    :unlike="unlike"
    :approve="approve"
    :disapprove="disapprove"
    :complete="complete"
    :translate="translateRev"
    :createValidationRequest="createValidationRequest"
    :cancelValidationRequest="cancelRequest"
    :goToTab="goToStep"
    :setCoach="setCoach"
    :canCompleteRev="canCompleteRev"
  >
    <div
      class="ma-0 pa-0"
      slot-scope="{
        isEditMode,
        organizationId,
        accountSiteId,
        accountId,
        edit,
        canComplete,
        delete: canDelete,
        isApproved,
        validate,
        iconOptions,
        menuOptions,
        revId,
        errors,
        revTags,
        saveRev,
        setValidationRequest,
        setCancelRequest,
        completeRev,
        likeRev,
        unlikeRev,
        approveRev,
        disapproveRev,
        handleCancelClick,
        setEditMode,
        setViewMode,
        setRevChanged,
        isSaving,
        translateRev,
        implementRev,
        highlightRev,
        isTranslating,
        isTranslated,
        hasCompleteError,
        removeApproval,
        approve,
        revApprovals,
        isImplementedValidated,
        hasRequestValidation,
        canDoFinancialValidation,
        implement,
        isLiked,
        showImplementError,
        plannerIntegration,
        replicateRev,
      }"
    >
      <v-container class="ma-0 pa-0">
        <ChangedStatusAlert
          v-if="hasChangedStatusReason"
          class="mt-1"
          data-testid="quickActionView-changedStatusAlert-alert"
          :data="rev"
        />

        <v-row class="my-0 py-0">
          <v-col md="8">
            <v-card elevation="2" rounded>
              <Header
                likeTid="quickAction-button-like"
                likedTid="quickAction-button-liked"
                shareType="QuickAction"
                revType="quickAction"
                :isEditMode="isEditMode"
                :organizationId="organizationId"
                :accountSiteId="accountSiteId"
                :accountId="accountId"
                :rev="rev"
                :mediaObject="backgroundHeaderImage"
                :availableOwners="availableOwners"
                :availableCoaches="availableCoaches"
                :availableCollaborators="availableCollaborators"
                :trackStartDelete="trackStartDelete"
                :trackCancelDelete="trackCancelDelete"
                :isLiked="isLiked"
                :isApproved="isApproved"
                :iconOptions="iconOptions"
                :menuOptions="menuOptions"
                :deleteRev="deleteRev"
                :setCoach="setCoach"
                :setOwner="setOwner"
                :setCollaborators="setCollaborators"
                :updateName="updateRevName"
                :isSaving="isSaving"
                :canSave="canSave"
                :trackUpdateRev="trackUpdateRev"
                :isTranslating="isTranslating"
                :showStatusModal="showStatusModal"
                :revApprovals="revApprovals"
                :isImplementedValidated="isImplementedValidated"
                :hasRequestValidation="hasRequestValidation"
                :plannerIntegration="plannerIntegration"
                @toggleTranslate="translateRev"
                @editRev="setEditMode"
                @save="saveRev"
                @like="likeRev"
                @approve="approveRev"
                @disapprove="disapproveRev"
                @unlike="unlikeRev"
                @revChanged="setRevChanged"
                @cancel="handleCancelClick"
                @validate="handleValidateClick"
                @statusChanged="
                  $event === $constants.revStatus.completed
                    ? completeRev(canCompleteRev)
                    : handleStatusChanged($event)
                "
                @hideModal="showStatusModal = false"
                @changeModalStatus="handleModalStatusChanged($event)"
                @implement="implementRev"
                @highlight="highlightRev"
                @replicateRev="replicateRev(rev)"
              />
            </v-card>

            <PredictedFinancialImpactAlert
              data-testid="quickActionView-alert-predictedFinancialImpact"
              :rev="rev"
            />
            <Stepper
              class="my-2"
              :steps="STEPS"
              :phases="phases"
              :activeStep="activeStep"
              :hasCompleteError="hasCompleteError"
              :hasRequestValidation="hasRequestValidation"
              :showImplementError="showImplementError"
              @click="goToStep($event, 'stepper')"
            />
            <v-card class="pa-8">
              <Before
                hasNextButton
                v-show="activeStep === STEPS.BEFORE"
                :isTranslating="isTranslating"
                :isSaving="isSaving"
                :isEditMode="isEditMode"
                :rev="rev"
                :markRequiredFields="hasCompleteError"
                @revChange="setRevChanged"
                @next="goToStep(2)"
              />
              <After
                hasNextButton
                hasBackButton
                v-show="activeStep === STEPS.AFTER"
                :isTranslating="isTranslating"
                :isSaving="isSaving"
                :isEditMode="isEditMode"
                :rev="rev"
                :markRequiredFields="hasCompleteError"
                :canComplete="canComplete"
                @revChange="setRevChanged"
                @completeRev="completeRev(canCompleteRev)"
                @goToStep="goToStep"
                @setViewMode="setViewMode"
                @setEditMode="setEditMode"
                @next="goToStep(3)"
                @back="goToStep(1)"
              />

              <Review
                hasBackButton
                v-show="activeStep === STEPS.REVIEW"
                :hasRequestValidation="hasRequestValidation"
                :isSaving="isSaving"
                :isTranslating="isTranslating"
                :isEditMode="isEditMode"
                :rev="rev"
                :canImplement="implement"
                :canDoFinancialValidation="canDoFinancialValidation"
                @implementRev="implementRev"
                @revChange="setRevChanged"
                @createValidationRequest="setValidationRequest"
                @cancelValidationRequest="setCancelRequest"
                @back="goToStep(2)"
                @validate="handleValidateClick"
              />
            </v-card>
          </v-col>
          <v-col md="4">
            <RevSidebar
              submitCommentTestId="quickAction_comments_submit"
              modelName="quick-actions"
              :modelType="$t(`common.${modelTypeName}_one`)"
              :modelId="revId"
              :hasCommentsActive="true"
              :hasChallengeActive="true"
              :hasAttachmentsActive="true"
              :canEdit="canComplete"
              :isEditMode="isEditMode"
              :rev="rev"
              :errors="errors"
              :tags="revTags"
              @priorityChanged="updatePriority"
              @onDueDateChange="updateDueDate"
              @onNotesChange="updateNotes"
              @revChanged="setRevChanged"
              @locationChange="locationChange"
            />
          </v-col>
        </v-row>
      </v-container>

      <FindingInfoModal
        :open="isFindingInfoModalOpen"
        @close="isFindingInfoModalOpen = false"
      />
    </div>
  </RevViewWrapper>
</template>

<script>
import _ from 'lodash'
import { mapState, mapMutations, mapActions } from 'vuex'

import { utils } from 'frontend-js-shared'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

import FindingInfoModal from './FindingInfoModal.vue'
import ChangedStatusAlert from '@/components/Core/ChangedStatusAlert.vue'
import Stepper from '@/components/Stepper'
import PredictedFinancialImpactAlert from '@/components/Rev/PredictedFinancialImpactAlert.vue'

const STEPS = {
  BEFORE: 1,
  AFTER: 2,
  REVIEW: 3,
}

export default {
  name: 'QuickActionView',

  mixins: [trackerMixin, mixin],

  components: {
    RevViewWrapper: () =>
      import('@/containers/RevViewWrapper/RevViewWrapper.vue'),
    Header: () => import('@/components/Rev/Header.vue'),
    RevSidebar: () => import('@/components/Rev/RevSidebar.vue'),
    Before: () => import('@/containers/QuickActionView/Before.vue'),
    After: () => import('@/containers/QuickActionView/After.vue'),
    Review: () => import('@/containers/QuickActionView/Review.vue'),
    FindingInfoModal,
    ChangedStatusAlert,
    Stepper,
    PredictedFinancialImpactAlert,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
    revId: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: false,
    },
    initialStep: {
      type: Number,
      required: false,
    },
  },

  data() {
    const isFromFinding = Boolean(this.$route?.params?.isFromFinding)

    return {
      activeStep: this.getInitialactiveStep(),
      canSave: true,
      isLoading: false,
      showValidate: false,
      isFindingInfoModalOpen: isFromFinding,
      STEPS,
      showStatusModal: false,
    }
  },

  mounted() {
    if (this.isFindingInfoModalOpen) return this.complete()
  },

  computed: {
    ...mapState('quickAction', {
      rev: state => _.get(state, 'rev'),
      availableOwners: state => _.get(state, 'availableOwners', []),
      availableCoaches: state => _.get(state, 'availableCoaches', []),
      availableCollaborators: state =>
        _.get(state, 'availableCollaborators', []),
    }),

    backgroundHeaderImage() {
      return _.get(this.rev.beforePhase, 'media[0]', null)
    },

    hasChangedStatusReason() {
      if (this.rev?.status == this.$constants.revStatus.canceled)
        return Boolean(this.rev?.canceledReason)
      if (this.rev?.status == this.$constants.revStatus.onHold)
        return Boolean(this.rev?.onHoldReason)
      return false
    },

    modelTypeName() {
      return _.snakeCase(this.rev.type)
    },

    hasBeforeMissingFields() {
      const { beforePhase = {} } = this.rev
      const { situation } = beforePhase

      return _.isEmpty(situation)
    },

    hasAfterMissingFields() {
      const { afterPhase = {} } = this.rev
      const { solution } = afterPhase

      return _.isEmpty(solution)
    },

    canCompleteRev() {
      return utils.revs.canComplete(this.rev)
    },

    isReviewComplete() {
      return (
        this.isObjectNotEmpty({
          ...this.rev?.review,
          ...this.rev?.reviewPhase,
        }) || Boolean(this.rev?.hasFinancialValidation)
      )
    },

    phases() {
      return [
        {
          name: this.$t('quick_action.phases.before'),
          complete: !this.hasBeforeMissingFields,
          missingFields: this.hasBeforeMissingFields,
          id: this.$constants.quickAction.phases.before,
        },
        {
          name: this.$t('quick_action.phases.after'),
          complete: !this.hasAfterMissingFields,
          missingFields: this.hasAfterMissingFields,
          id: this.$constants.quickAction.phases.after,
        },
        {
          name: this.$t('rev.validation.validate'),
          complete: this.isReviewComplete,
          missingFields: false,
          id: this.$constants.quickAction.phases.review,
        },
      ]
    },
  },

  methods: {
    ...mapMutations({
      resetRev: 'quickAction/resetRev',
      setCoach: 'quickAction/setCoach',
      setOwner: 'quickAction/setOwner',
      setCollaborators: 'quickAction/setCollaborators',
      updateRevName: 'quickAction/updateRevName',
      updateLocation: 'quickAction/updateLocation',
      updateChallege: 'quickAction/updateChallege',
      updatePriority: 'quickAction/updatePriority',
      updateDueDate: 'quickAction/updateDueDate',
      updateNotes: 'quickAction/updateNotes',
    }),

    ...mapActions({
      get: 'quickAction/getRev',
      update: 'quickAction/update',
      like: 'quickAction/like',
      unlike: 'quickAction/unlike',
      approve: 'quickAction/approveRev',
      disapprove: 'quickAction/disapproveRev',
      delete: 'quickAction/delete',
      translate: 'quickAction/translateRev',
      createValidationRequest: 'quickAction/validationRequest',
      cancelRequest: 'quickAction/updateRequestStatus',
    }),

    getInitialactiveStep() {
      const isFromFinding = Boolean(this.$route?.params?.isFromFinding)
      if (isFromFinding) return STEPS.AFTER

      return Number(this.initialStep ?? this.$route.query?.tabIndex ?? 1)
    },

    complete() {
      return this.update({
        organizationId: this.organizationId,
        revId: this.rev.id,
        data: { ...this.rev, status: 'completed' },
      }).then(() => {
        this.get({
          organizationId: this.organizationId,
          revId: this.rev.id,
        })
      })
    },

    updateStatus(payload) {
      return this.update({
        organizationId: this.organizationId,
        revId: this.rev.id,
        data: { ...this.rev, ...payload },
      }).then(() => {
        this.get({
          organizationId: this.organizationId,
          revId: this.rev.id,
        })
      })
    },

    trackStatusChanged(status, reason) {
      this.trackRevStatusChanged(
        this.rev,
        this.isEditMode,
        'selector',
        status,
        reason
      )
    },
    sendPayloadToUpdateStatus(payload) {
      if (payload.status == this.$constants.revStatus.canceled)
        return {
          ...this.rev,
          status: payload.status,
          canceledReason: payload.reason,
        }
      return {
        ...this.rev,
        status: payload.status,
        onHoldReason: payload.reason,
      }
    },

    handleModalStatusChanged(payload) {
      if (payload.status == this.$constants.revStatus.canceled) {
        this.updateStatus({
          canceledReason: payload.reason,
          status: payload.status,
        })
        this.trackStatusChanged(payload.status, {
          canceledReason: Boolean(payload.reason),
        })
      }
      if (payload.status == this.$constants.revStatus.onHold) {
        this.updateStatus({
          onHoldReason: payload.reason,
          status: payload.status,
        })
        this.trackStatusChanged(payload.status, {
          onHoldReason: Boolean(payload.reason),
        })
      }
      this.showStatusModal = false
    },

    handleStatusChanged(status) {
      if (
        status === this.$constants.revStatus.canceled ||
        status === this.$constants.revStatus.onHold
      )
        return (this.showStatusModal = true)
      this.updateStatus({ status })
      this.trackStatusChanged(status, null)
    },

    locationChange(attrs) {
      if (attrs.site) {
        this.canSave = true
        this.updateLocation({
          ...attrs,
          organization: { _id: this.organizationId },
        })
      } else {
        this.canSave = false
      }
    },

    deleteRev() {
      return this.delete({
        organizationId: this.organizationId,
        revId: this.rev.id,
      })
        .then(() =>
          this.trackDelete(this.rev, 'quickAction', 'complete', 'success')
        )
        .catch(err =>
          this.trackDelete(this.rev, 'quickAction', 'complete', 'fail', err)
        )
    },

    trackStartDelete() {
      this.trackDelete(this.rev, 'quickAction', 'start')
    },

    trackCancelDelete() {
      this.trackDelete(this.rev, 'quickAction', 'cancel')
    },

    trackUpdateRev(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    goToStep(step, from = 'button') {
      this.trackRevNavigate(this.rev, this.phases[step - 1].id, from)

      if (step === this.activeStep) return

      this.activeStep = step

      this.$router.replace({
        path: this.$route.fullPath,
        query: { ...this.$route.query, tabIndex: step },
      })
    },

    handleValidateClick() {
      this.trackClickValidateIcon({ action: 'goToValidationView' })
      return this.navigateTo({
        name: 'validateRev',
        params: {
          revId: this.revId,
          revType: 'quick-action',
        },
      })
    },

    async translateRev() {
      try {
        const fieldsToTranslate = [
          'name',
          'afterPhase.solution',
          'beforePhase.situation',
          'reviewPhase.estimationExplanation',
        ]

        await this.translate({
          revId: this.revId,
          organizationId: this.organizationId,
          targetLanguage: this.getLocaleWithoutDialect(
            this.$store.getters.language
          ),
          fieldsToTranslate,
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    eventHub.$emit(HUB_EVENTS.REV_VIEW.BEFORE_ROUTE_LEAVE, { to, from, next })
  },
}
</script>
